import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { usePositionContext } from '../contexts/PositionContext';
import PortfolioRequired from '../components/PortfolioRequired';
import { formatCurrency, formatChangedAmount, formatPercentage, formatPrice, parseFloatFromString } from '../utils/formatters';
import ImportExitLevelsModal from '../components/ImportExitLevelsModal';
import { useMutation } from '@apollo/client';
import { EXPORT_EXIT_LEVELS_CSV } from '../graphql/mutations/exitLevels';
import { downloadCSV } from '../utils/fileExport';
import Tooltip from '../components/common/Tooltip';
import EmptyState from '../components/EmptyState';
import { tokenRoutePath } from '../utils/tokenRoutes';

const ExitsPage = () => {
  const { currentPortfolioId, currentPortfolio } = usePortfolioContext();
  const { positions, loading, error } = usePositionContext();
  const navigate = useNavigate();

  const positionsWithExits = useMemo(() => {
    return (positions || []).filter(p => p?.exitStrategy?.levels && p.exitStrategy.levels.length > 0);
  }, [positions]);

  const [sortBy, setSortBy] = useState('strategyDelta');
  const [sortDir, setSortDir] = useState('asc');
  const [sortInitialized, setSortInitialized] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exportExitLevels] = useMutation(EXPORT_EXIT_LEVELS_CSV);

  const handleExport = async () => {
    try {
      const { data } = await exportExitLevels({ variables: { portfolioId: currentPortfolioId } });
      const res = data?.exportExitLevelsCSV;
      if (!res?.success) return;
      downloadCSV(res.csvData || '', currentPortfolio?.name, 'exit_levels');
    } catch (_) {}
  };

  // Persist sort state to localStorage (similar to Positions page)
  const EXITS_SORT_STATE_KEY = 'exitsTableSortState';

  // Load saved sort state once
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(EXITS_SORT_STATE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          if (typeof parsed.sortBy === 'string') setSortBy(parsed.sortBy);
          if (parsed.sortDir === 'asc' || parsed.sortDir === 'desc') setSortDir(parsed.sortDir);
        }
      }
    } catch (_) {}
    setSortInitialized(true);
  }, []);

  // Save sort state whenever it changes (after initialization)
  React.useEffect(() => {
    if (!sortInitialized) return;
    try {
      localStorage.setItem(EXITS_SORT_STATE_KEY, JSON.stringify({ sortBy, sortDir }));
    } catch (_) {}
  }, [sortBy, sortDir, sortInitialized]);

  const handleSort = (field) => {
    if (field === sortBy) {
      const nextDir = sortDir === 'asc' ? 'desc' : 'asc';
      console.log('[Exits] handleSort toggle', { field, prevSortBy: sortBy, prevSortDir: sortDir, nextSortBy: field, nextSortDir: nextDir });
      setSortDir(nextDir);
    } else {
      const nextDir = field === 'strategyDelta' ? 'asc' : 'desc';
      console.log('[Exits] handleSort change', { field, prevSortBy: sortBy, prevSortDir: sortDir, nextSortBy: field, nextSortDir: nextDir });
      setSortBy(field);
      setSortDir(nextDir);
    }
  };

  const getSortIndicator = (field) => {
    if (field !== sortBy) return null;
    return sortDir === 'asc' ? '↑' : '↓';
  };

  const sorted = useMemo(() => {
    const getLevelTextColor = (currentPriceValue, level) => {
      if (!level) return 'text-gray-400 dark:text-gray-500';
      const levelPrice = parseFloatFromString(level.price);
      const status = level.status;
      if (status === 'FILLED') return 'text-green-500 dark:text-green-400';
      if (status === 'ORDER_SET') return 'text-blue-500 dark:text-blue-400';
      if (currentPriceValue > levelPrice) return 'text-red-500 dark:text-red-400';
      if (currentPriceValue >= levelPrice * 0.8) return 'text-yellow-500 dark:text-yellow-400';
      return 'text-gray-600 dark:text-gray-300';
    };

    const asRows = (positionsWithExits || []).map((position) => {
      const token = position.token || {};
      const marketData = token.marketData || {};
      const currentPrice = parseFloatFromString(marketData.currentPrice || '0');
      const holdingsValueNumeric = parseFloatFromString(position.holdingsValue || '0');
      const holdingsAmountNumeric = parseFloatFromString(position.holdingsAmount || '0');
      const plannedProceeds = parseFloatFromString(position.exitStrategy?.proceeds || '0');
      const plannedCost = parseFloatFromString(position.exitStrategy?.cost || '0');
      const plannedPnl = plannedProceeds - plannedCost;
      const plannedRoi = plannedCost > 0 ? (plannedPnl / plannedCost) * 100 : 0;
      const plannedMultiple = plannedCost > 0 ? (plannedProceeds / plannedCost) : null;

      const levels = [...(position.exitStrategy?.levels || [])].sort((a, b) => parseFloatFromString(a.price) - parseFloatFromString(b.price));
      const counts = {
        filled: levels.filter(l => (l.status || '').toUpperCase() === 'FILLED').length,
        orderSet: levels.filter(l => (l.status || '').toUpperCase() === 'ORDER_SET').length,
        planned: levels.filter(l => (l.status || 'PLANNED').toUpperCase() === 'PLANNED').length,
        total: levels.length
      };

      const levelInfos = levels.map((l, idx) => {
        const lp = parseFloatFromString(l.price);
        const amt = parseFloatFromString(l.amount);
        const changePct = currentPrice > 0 ? ((lp / currentPrice) - 1) * 100 : 0;
        const sign = changePct > 0 ? '+' : '';
        return {
          label: `L${idx + 1}`,
          deltaText: `${sign}${changePct.toFixed(1)}%`,
          numericDelta: changePct,
          status: (l.status || 'PLANNED').toUpperCase(),
          colorClass: getLevelTextColor(currentPrice, l),
          price: lp,
          amount: amt,
        };
      });

      // Delta of the first non-filled level (by ascending level price).
      // If no non-filled levels remain, use +Infinity to push it to the bottom when sorting ascending (closest first).
      const firstNonFilled = levelInfos.find(li => li.status !== 'FILLED');
      const firstNonFilledDelta = firstNonFilled ? firstNonFilled.numericDelta : Number.POSITIVE_INFINITY;

      // Planned to sell percent = sum(non-filled amounts) / current holdings amount
      const plannedSellAmount = levelInfos.filter(li => li.status !== 'FILLED').reduce((sum, li) => sum + (li.amount || 0), 0);
      const plannedSellPercent = holdingsAmountNumeric > 0 ? (plannedSellAmount / holdingsAmountNumeric) * 100 : 0;

      const row = {
        position,
        token,
        marketData,
        currentPrice,
        holdingsValueNumeric,
        holdingsAmountNumeric,
        plannedProceeds,
        plannedCost,
        plannedPnl,
        plannedRoi,
        plannedMultiple,
        levels,
        counts,
        levelInfos,
        firstNonFilledDelta,
        plannedSellPercent,
      };
      console.log('[Exits] row computed', token.ticker || token.name, { currentPrice, firstNonFilledDelta, levelInfos: levelInfos.map(li => ({ status: li.status, delta: li.numericDelta })) });
      return row;
    });

    console.groupCollapsed('[Exits] sort input');
    console.log('sortBy:', sortBy, 'sortDir:', sortDir);
    try {
      console.table(asRows.map(r => ({ asset: r.token.ticker || r.token.name, firstNonFilledDelta: r.firstNonFilledDelta })));
    } catch (_) {
      console.log(asRows.map(r => ({ asset: r.token.ticker || r.token.name, firstNonFilledDelta: r.firstNonFilledDelta })));
    }
    console.groupEnd();

    const getValue = (row, field) => {
      switch (field) {
        case 'token.name':
          return row.token.name || row.token.ticker || '';
        case 'currentPrice':
          return row.currentPrice;
        case 'plannedProceeds':
          return row.plannedProceeds;
        case 'plannedPnl':
          return row.plannedPnl;
        case 'plannedRoi':
          return row.plannedRoi;
        case 'holdingsValue':
          return row.holdingsValueNumeric;
        case 'plannedSellPercent':
          return row.plannedSellPercent;
        case 'levelsFilled':
          return row.counts.filled;
        case 'levelsTotal':
          return row.counts.total;
        case 'strategyDelta':
          return row.firstNonFilledDelta;
        default:
          return 0;
      }
    };

    const sortedRows = [...asRows].sort((a, b) => {
      const aValue = getValue(a, sortBy);
      const bValue = getValue(b, sortBy);
      if (sortBy === 'strategyDelta') {
        // Ensure numeric comparison, handle NaN/Infinity
        const aNum = Number.isFinite(aValue) ? aValue : Number.POSITIVE_INFINITY;
        const bNum = Number.isFinite(bValue) ? bValue : Number.POSITIVE_INFINITY;
        return sortDir === 'asc' ? (aNum - bNum) : (bNum - aNum);
      }
      if (typeof aValue === 'string' || typeof bValue === 'string') {
        return sortDir === 'asc' ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
      }
      return sortDir === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
    });

    console.groupCollapsed('[Exits] sort output');
    try {
      console.table(sortedRows.map(r => ({ asset: r.token.ticker || r.token.name, firstNonFilledDelta: r.firstNonFilledDelta })));
    } catch (_) {
      console.log(sortedRows.map(r => ({ asset: r.token.ticker || r.token.name, firstNonFilledDelta: r.firstNonFilledDelta })));
    }
    console.groupEnd();

    return sortedRows;
  }, [positionsWithExits, sortBy, sortDir]);

  const aggregateStats = useMemo(() => {
    const rows = sorted || [];
    const totalProceeds = rows.reduce((sum, r) => sum + (typeof r.plannedProceeds === 'number' ? r.plannedProceeds : parseFloatFromString(r.plannedProceeds || 0)), 0);
    const totalCost = rows.reduce((sum, r) => sum + (typeof r.plannedCost === 'number' ? r.plannedCost : parseFloatFromString(r.plannedCost || 0)), 0);
    const totalRoiPct = totalCost > 0 ? ((totalProceeds - totalCost) / totalCost) * 100 : 0;
    const totalMultiple = totalCost > 0 ? (totalProceeds / totalCost) : 0;
    return { totalProceeds, totalRoiPct, totalMultiple };
  }, [sorted]);

  if (!currentPortfolioId) {
    return <PortfolioRequired title="Exits" description="Please select a portfolio to view exit strategies." />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error.message}</span>
      </div>
    );
  }

  // Do not early-return when there are no strategies so the Import button is still visible

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Exit strategies</h1>
        <div className="mt-1 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center flex-wrap gap-2">
            <span>
              <span className="text-gray-900 dark:text-white font-semibold">{positionsWithExits.length}</span>
              {" "}out of{" "}
              <span className="text-gray-900 dark:text-white font-semibold">{(positions || []).length}</span>{" "}assets
            </span>
            {positionsWithExits.length > 0 && (
              <>
                <span className="opacity-60">•</span>
                <span>
                  Planned proceeds: <span className="text-gray-900 dark:text-white font-semibold">{formatCurrency(aggregateStats.totalProceeds)}</span>
                </span>
                <span className="opacity-60">•</span>
                <span>
                  Total ROI: <span className="text-gray-900 dark:text-white font-semibold">{formatPercentage(aggregateStats.totalRoiPct)}</span>
                </span>
                <span className="opacity-60">•</span>
                <span>
                  Multiple: <span className="text-gray-900 dark:text-white font-semibold">{aggregateStats.totalMultiple.toFixed(1)}x</span>
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowImport(true)} className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none">
              Import CSV
            </button>
            {positionsWithExits.length > 0 && (
              <button onClick={handleExport} className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none">
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
        {(!positionsWithExits || positionsWithExits.length === 0) ? (
          <EmptyState
            title="No exit strategies found"
            description="Create exit levels on a token page to see them here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('token.name')}
                  >
                    Asset {getSortIndicator('token.name')}
                  </th>
                  <th 
                    className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('currentPrice')}
                  >
                    Price {getSortIndicator('currentPrice')}
                  </th>
                  <th 
                    className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('holdingsValue')}
                  >
                    Holdings {getSortIndicator('holdingsValue')}
                  </th>
                  <th 
                    className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('plannedProceeds')}
                  >
                    Planned Proceeds {getSortIndicator('plannedProceeds')}
                  </th>
                  <th 
                    className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('plannedSellPercent')}
                  >
                    Planned to sell {getSortIndicator('plannedSellPercent')}
                  </th>
                  <th 
                    className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('plannedPnl')}
                  >
                    Planned PnL {getSortIndicator('plannedPnl')}
                  </th>
                  <th 
                    className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('plannedRoi')}
                  >
                    Planned ROI {getSortIndicator('plannedRoi')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('strategyDelta')}
                  >
                    Strategy {getSortIndicator('strategyDelta')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sorted.map(({ position, token, marketData, holdingsValueNumeric, plannedProceeds, plannedPnl, plannedRoi, plannedMultiple, plannedSellPercent, counts, levelInfos }) => {
                  return (
                    <tr 
                      key={position.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => navigate(tokenRoutePath(token))}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400">
                        {token.ticker || token.name}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">{formatPrice(marketData.currentPrice)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                        {formatCurrency(holdingsValueNumeric)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">{formatCurrency(plannedProceeds)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right">{formatPercentage(plannedSellPercent)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right">{formatChangedAmount(plannedPnl)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                        {formatPercentage(plannedRoi)}
                        {plannedPnl !== 0 && plannedMultiple ? (
                          <span className="ml-1 text-xs">({plannedMultiple.toFixed(1)}x)</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-left">
                        <div className="flex flex-wrap gap-2 justify-start items-center">
                          {levelInfos.length > 0 ? (
                            levelInfos.map((li, i) => {
                              const statusText = li.status === 'FILLED' ? 'Filled' : (li.status === 'ORDER_SET' ? 'Order set' : 'Planned sell');
                              const tooltipTitle = `${statusText}: ${li.amount} ${token.ticker || ''} @ ${formatPrice(li.price)} = ${formatPrice(li.amount * li.price)}`;
                              return (
                                <span key={i} className="flex items-center gap-1">
                                  <Tooltip title={tooltipTitle} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={10}>
                                    <span className={`text-sm select-none ${li.colorClass}`}>■</span>
                                  </Tooltip>
                                  <Tooltip title={tooltipTitle} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={10}>
                                    <span className={`px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 ${li.colorClass}`}>
                                      {li.status !== 'FILLED' ? `${li.deltaText}` : `FILLED`}
                                    </span>
                                  </Tooltip>
                                </span>
                              );
                            })
                          ) : (
                            <span>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showImport && (
        <ImportExitLevelsModal isOpen={showImport} onClose={() => setShowImport(false)} portfolioId={currentPortfolioId} onSuccess={() => { /* maybe refetch via PositionProvider */ }} />
      )}
    </div>
  );
};

export default ExitsPage;


